import React, { useEffect, useState } from 'react';
import { Loader2, X, AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';

interface SourceRow {
  name: string;
  total: number;
  searchable: number;
}

interface JurisdictionSourcesData {
  sources: SourceRow[];
  total: { artifacts: number; searchable: number };
}

interface Props {
  jurisdictionName: string;
  open: boolean;
  onClose: () => void;
}

export const JurisdictionSourcesModal: React.FC<Props> = ({
  jurisdictionName,
  open,
  onClose,
}) => {
  const [data, setData] = useState<JurisdictionSourcesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !jurisdictionName) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(
          `/api/source-health/jurisdiction-by-name/${encodeURIComponent(jurisdictionName)}/sources`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json.data as JurisdictionSourcesData);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, jurisdictionName]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/30" />
      <div
        className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-lg max-h-[85vh] overflow-y-auto z-10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Source Breakdown
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-300 text-red-800 rounded-xl p-4 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 text-red-600" />
              {error}
            </div>
          )}

          {data && !loading && (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="pb-2 text-left text-xs font-bold text-gray-700">
                      Source
                    </th>
                    <th className="pb-2 text-right text-xs font-bold text-gray-700">
                      Total
                    </th>
                    <th className="pb-2 text-right text-xs font-bold text-gray-700">
                      Searchable
                    </th>
                    <th className="pb-2 text-right text-xs font-bold text-gray-700">
                      %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.sources.map((s) => (
                    <tr key={s.name} className="border-b border-gray-100">
                      <td className="py-2 pr-4 text-sm text-gray-900">
                        {s.name}
                      </td>
                      <td className="py-2 text-sm text-gray-600 text-right">
                        {s.total.toLocaleString()}
                      </td>
                      <td className="py-2 text-sm text-gray-600 text-right">
                        {s.searchable.toLocaleString()}
                      </td>
                      <td className="py-2 text-sm text-right">
                        <span
                          className={
                            s.total > 0 && s.searchable === s.total
                              ? 'text-green-600 font-semibold'
                              : s.total > 0
                                ? 'text-amber-600 font-semibold'
                                : 'text-gray-400'
                          }
                        >
                          {s.total > 0
                            ? ((s.searchable / s.total) * 100).toFixed(2)
                            : '0.00'}
                          %
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total row */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-200 font-semibold text-sm text-gray-900">
                <span>Total</span>
                <div className="flex items-center gap-6">
                  <span>{data.total.artifacts.toLocaleString()}</span>
                  <span>{data.total.searchable.toLocaleString()}</span>
                  <span>
                    {data.total.artifacts > 0
                      ? ((data.total.searchable / data.total.artifacts) * 100).toFixed(2)
                      : '0.00'}
                    %
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
